import axios, { AxiosError, type InternalAxiosRequestConfig } from 'axios'
import {
  errorCodeOf,
  isVerificationRoute,
  routeForVerificationCode,
  verificationUrl,
} from '@/features/auth/utils/verification'

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:5000'

export const api = axios.create({
  baseURL: `${API_URL}/api`,
  headers: {
    'Content-Type': 'application/json'
  }
})

api.interceptors.request.use((config) => {
  const token = localStorage.getItem('accessToken')
  if (token) {
    config.headers.Authorization = `Bearer ${token}`
  }
  return config
})

type RetriableRequest = InternalAxiosRequestConfig & { _retried?: boolean }

/** Raised when there is no stored refresh token: the user must sign in again. */
class SessionExpiredError extends Error {}

let refreshInFlight: Promise<string> | null = null

function clearSession(): void {
  localStorage.removeItem('accessToken')
  localStorage.removeItem('refreshToken')
}

/**
 * Exchanges the stored refresh token for a new pair. A single request is shared
 * by every concurrent caller because the backend rotates refresh tokens on use:
 * a second parallel call would present an already-revoked token.
 */
function refreshAccessToken(): Promise<string> {
  if (refreshInFlight) return refreshInFlight

  const refreshToken = localStorage.getItem('refreshToken')
  if (!refreshToken) return Promise.reject(new SessionExpiredError())

  refreshInFlight = axios
    .post(`${API_URL}/api/auth/refresh`, { refreshToken })
    .then((res) => {
      const { accessToken, refreshToken: newRefresh } = res.data
      localStorage.setItem('accessToken', accessToken)
      localStorage.setItem('refreshToken', newRefresh)
      return accessToken as string
    })
    .finally(() => {
      refreshInFlight = null
    })

  return refreshInFlight
}

function isDefinitiveAuthFailure(error: unknown): boolean {
  if (error instanceof SessionExpiredError) return true
  const status = (error as AxiosError)?.response?.status
  return status === 400 || status === 401 || status === 403
}

/**
 * R7 forced verification: the backend answers non-allowlisted requests from
 * unverified users with 403 + `PhoneVerificationRequired` /
 * `KycVerificationRequired`. Send the user to the flow that clears it,
 * carrying the current location as `?next=` so they land back after
 * verifying. Never fires without a session (anonymous 403s are unrelated),
 * from inside a verification flow, or from the sign-in screen — otherwise
 * the user could never complete verification.
 */
function redirectForVerification(error: AxiosError): void {
  if (typeof window === 'undefined') return
  const target = routeForVerificationCode(errorCodeOf(error))
  if (!target) return
  if (!localStorage.getItem('accessToken')) return
  const pathname = window.location.pathname
  if (pathname === target || isVerificationRoute(pathname)) return
  if (pathname === '/login') return
  window.location.assign(verificationUrl(target, `${pathname}${window.location.search}`))
}

api.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    if (error.response?.status === 403) redirectForVerification(error)
    const originalRequest = error.config as RetriableRequest | undefined
    if (!originalRequest || error.response?.status !== 401 || originalRequest._retried) {
      return Promise.reject(error)
    }
    originalRequest._retried = true

    try {
      const accessToken = await refreshAccessToken()
      originalRequest.headers.Authorization = `Bearer ${accessToken}`
      return api(originalRequest)
    } catch (refreshError) {
      // Only a definitive rejection ends the session. A network blip or a 5xx
      // must not log the user out: the next request will try again.
      if (isDefinitiveAuthFailure(refreshError)) {
        clearSession()
        window.location.href = '/login'
      }
      return Promise.reject(error)
    }
  }
)

// --- KYC (initial submission for Creator/Agency accounts) ---

export type KycStatus = 'none' | 'pending' | 'verified' | 'rejected'

/** Raw backend value (PascalCase via JsonStringEnumConverter); run it through `normalizeKycStatus`. */
export type KycStatusWire = 'None' | 'Pending' | 'Verified' | 'Rejected'

export interface KycStatusDto {
  kycStatus: KycStatusWire
  submittedAt: string | null
  reviewedAt: string | null
  retryCount: number
  adminReason: string | null
  /** True for the latest submission when the submitter's gender was `unspecified`. Absent on older payloads. */
  requiresExtendedReview?: boolean
}

export interface KycSubmissionResponseDto {
  documentId: string
  kycStatus: KycStatusWire
  submittedAt: string
}

export async function getKycStatus(): Promise<KycStatusDto> {
  const res = await api.get('/users/me/kyc/status')
  return res.data
}

export async function submitKyc(
  file: File,
  _mimeType: string
): Promise<KycSubmissionResponseDto> {
  const formData = new FormData()
  formData.append('file', file, file.name)
  return api.post('/users/me/kyc/submit', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  }).then((res) => res.data)
}
